import type { Key } from 'react';
import type React from 'react';

import type { TableProps } from 'antd';
import type { TableRowSelection } from 'antd/es/table/interface';
import { SortingOrder } from 'chaika-enums';

import type { ColumnsType } from '../types';
import type { MeatballButtonMenuItem } from '@components/ActionButtons/MeatballButton';
import type { PresetFiltersParams } from '@components/Table/filters/PresetFilters/PresetFilters';
import type { TableActionParams } from '../SelectedRowsActionButton/SelectedRowsActionButton';
import type { SearchParams } from '../SearchBar/SearchBar';
import type { ButtonParams } from '../ControlButton/ControlButton';
import type { TitleModifiers } from '../Title/Title';
import type { TableMainActionParams } from '../SelectedRowsMainActionButtons/SelectedRowsMainActionButtons';
import type { RowForceUnselectParams } from '../Checkbox/hooks/useCheckboxes';

export interface RowHandlers {
    onClick?(event: React.MouseEvent<HTMLDivElement>): void;
    onDoubleClick?(event: React.MouseEvent<HTMLDivElement>): void;
    onMouseMove?(event: React.MouseEvent<HTMLDivElement>): void;
    onMouseLeave?(event: React.MouseEvent<HTMLDivElement>): void;
}

export interface RowSelectionParams extends TableRowSelection<any> {
    setSelectedRowsKeys(selectedRows: any[]): void;
}

export enum TableAction {
    Filter = 'filter',
    Sort = 'sort',
    Paginate = 'paginate',
}

export interface VirtualTableExpandable {
    expandedRowKeys?: Key[];
    expandedRowRender?(record: any, index: number, indent: number, expanded: boolean): React.ReactNode;
    expandIcon?(props: {
        expanded: boolean;
        onExpand: (record: any, e: React.MouseEvent<HTMLElement>) => void;
        record: any;
    }): React.ReactNode;
    rowExpandable?(record: any): boolean;
    columnWidth?: number;
}

export interface VirtualTableProps extends Pick<
    TableProps<any>,
    'dataSource' | 'loading' | 'footer' | 'rowClassName' | 'onChange'
> {
    withoutBorderRadius?: boolean;
    tableTitle?: string;
    count?: number;
    withoutCount?: boolean;
    withCheckboxes?: boolean;
    withSelectedCount?: boolean;
    selectedFilters?: Record<any, any>;
    presetFiltersParams?: PresetFiltersParams;
    mainButtonParams?: ButtonParams;
    smallButtonParams?: ButtonParams[];
    additionalButtonsParams?: ButtonParams[];
    height?: number;
    minHeight?: number;
    emptyText?: string;
    rowSelection?: RowSelectionParams;
    rowKey: string | ((record: any) => string);
    actions?: MeatballButtonMenuItem[];
    selectedRowsActions?: TableActionParams[];
    rowMainActionParams?: TableMainActionParams[];
    searchParams?: SearchParams;
    columns: ColumnsType<any>;
    /** Префикс ключей фильтров/поиска таблицы в URL; уникальный, если на странице несколько таблиц. */
    urlKey?: string;
    titleModifiers?: TitleModifiers[];
    lastViewedItems?: string[];
    rowForceUnselectParams?: RowForceUnselectParams;
    expandable?: VirtualTableExpandable;
    disableMinHeight?: boolean;
    onFiltersChange?(params: Record<any, any>): Promise<void> | void;
    onLoadMore?(): Promise<void>;
    onRow?(record: any): RowHandlers;
    onSortingOrderChange?(order: SortingOrder): Promise<void>;
}
